/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import OrderDetailsDeliverySchedule from "@eisinc/App/Components/OrderDetails/OrderDetailsDeliverySchedule";
import OrderDetailsInfoDisplay from "@eisinc/App/Components/OrderDetails/OrderDetailsInfoDisplay";
import OrderDetailsShipmentDetails from "@eisinc/App/Components/OrderDetails/OrderDetailsShipmentDetails";
import { OrderDetailsTableStyle } from "@eisinc/App/SharedStyles/OrderDetailsStyles";
import { isProductInActive } from "@eisinc/App/Utilities/helpers";
import LazyImage, { LazyImageProps } from "@eisinc/MobiusOverrides/LazyImage";
import Tooltip from "@eisinc/MobiusOverrides/Tooltip/Tooltip";
import ProductAddToCartButton from "@eisinc/Overrides/Components/ProductAddToCartButton";
import ProductDescription, { ProductDescriptionStyles } from "@eisinc/Overrides/Components/ProductDescription";
import { getOrderLineShipmentData } from "@eisinc/Overrides/Pages/OrderDetailsPage";
import { screen } from "@eisinc/XDL/Helpers";
import AddOutline from "@eisinc/XDL/Icons/AddOutline";
import ChevronDown from "@eisinc/XDL/Icons/ChevronDown";
import ChevronUp from "@eisinc/XDL/Icons/ChevronUp";
import { customBadgesStyles, xScreen } from "@eisinc/XDL/Utilities";
import printStyles from "@eisinc/XDL/Utilities/print";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import translate from "@insite/client-framework/Translate";
import { OrderLineModel, WishListSettingsModel } from "@insite/client-framework/Types/ApiModels";
import { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import { IconWrapper } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import get from "@insite/mobius/utilities/get";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Color from "color";
import * as React from "react";
import { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, props: OwnProps) => ({
    productInfo: getProductInfoFromList(state, "orderDetails", props.orderLine.productId as string),
});

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
};

interface OwnProps {
    orderLine: OrderLineModel;
    headerColumns: string[];
    wishListSettings: WishListSettingsModel;
    numberOfColumn: number;
    productInfo?: ProductInfo;
}

export interface OrderDetailsOrderLineProps {
    tableRowStyle?: DataTableRowProps;
    tableCellGridContainerStyle?: GridContainerProps;
    productDetailsGridStyle: GridItemProps;
    customWidthGridStyle: GridItemProps;
    fullWidthGridStyle: GridItemProps;
    halfWidthGridStyle: GridItemProps;
    productImageGridStyle: GridItemProps;
    productImageStyle: LazyImageProps;
    badgesContainer: InjectableCss;
    badgeImage: LazyImageProps;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    actionTableCellGridStyle: GridItemProps;
    addToListLink: LinkPresentationProps;
    errorMessage: TypographyProps;
    notProcessedStyle: TypographyProps;
    tableCellStyle?: DataTableCellProps;
}

export const orderDetailsOrderLineStyles: OrderDetailsOrderLineProps = {
    productDetailsGridStyle: {
        width: [12, 12, 12, 9, 9],
        css: css`
            ${screen("gt", "md")`
                padding-left: 8px;
            `}
        `,
    },
    productBrandStyles: {
        nameLink: {
            color: "text.main",
            css: css`
                ${TypographyStyle} {
                    font-weight: 500;
                }
            `,
        },
    },
    productDescriptionStyles: {
        productDetailLink: {
            color: "text.main",
            typographyProps: {
                weight: 500,
            },
        },
        productDetailDescription: {
            weight: 500,
        },
    },
    customWidthGridStyle: {
        width: [12, 5, 5, 12, 12],
        css: css`
            @media print {
                max-width: 100%;
                flex-basis: 100%;
            }

            input {
                max-width: 75px;
            }
            ${xScreen("lte", "md")} {
                flex-direction: column;
            }
        `,
    },
    fullWidthGridStyle: {
        width: 12,
        css: css`
            white-space: normal;
            @media print {
                &&&.quote-cell-print {
                    display: block;
                }
            }
        `,
    },
    halfWidthGridStyle: {
        width: 6,
        css: css`
            padding-top: 15px;
            &:nth-child(odd) {
                padding-left: 0;
            }
            @media print {
                &.quote-cell-print {
                    display: block;
                }
                &.quote-order-details-print {
                    width: 100%;
                    max-width: 100%;
                }
            }
        `,
    },
    actionTableCellGridStyle: {
        width: [6, 6, 6, 12, 12],
        css: css`
            flex-direction: column;
            word-break: keep-all;
            white-space: break-spaces;
            flex-direction: column;
        `,
    },
    productImageGridStyle: {
        width: [12, 12, 12, 3, 3],
        css: css`
            max-width: 100px;
            flex-direction: column;

            ${screen("lte", "md")`
                flex-direction: row;
                &&& {
                    margin-bottom: 15px; 
                }
            `}
        `,
    },
    productImageStyle: {
        css: css`
            border: 1px solid ${getColor("common.ui4")};
            padding: 1px;
        `,
    },
    badgesContainer: {
        css: css`
            ${customBadgesStyles.container.css}
            margin-top: 10px;
            ${screen("lte", "md")`
                margin: 0 10px;
            `}
        `,
    },
    badgeImage: {
        width: "20px",
        height: "20px",
        css: css`
            cursor: pointer;
            margin-right: 10px;

            ${printStyles.hideOnPrint}
            img {
                width: auto;
                height: auto;
                max-width: 100%;
                max-height: 100%;
            }
        `,
    },
    addToListLink: {
        typographyProps: {
            size: "14px",
        },
        icon: {
            iconProps: {
                src: AddOutline,
                size: 14,
                color: "primary.main",
            },
            position: "left",
        },
        css: css`
            margin-bottom: 20px;
        `,
    },
    errorMessage: {
        color: "danger",
        lineHeight: 1,
    },
    notProcessedStyle: {
        as: "div",
        css: css`
            width: 100%;
            text-align: right;
            ${screen("lte", "md")`text-align: center;`}
        `,
    },
    tableCellStyle: {
        css: css`
            @media print {
                display: block;
                width: 100%;
            }
            ${screen("lte", "md")`
            &&&& {
                height: auto;
            }
        `}
        `,
    },
};

const tableRowWithAccordionStyle = {
    evenRowCss: css`
        background: transparent;
    `,
    css: css`
        ${orderDetailsOrderLineStyles.tableRowStyle?.css}
        border-bottom: 0;
        &&&& {
            background-color: ${getColor("common.accent")};
        }
        td {
            padding: 16px 12px 0;
            ${screen("lte", "md")`
                display: flex;
                padding: 6px 12px;
            `}
        }
    `,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const OrderDetailsOrderLine: FC<Props> = ({
    orderLine,
    headerColumns,
    numberOfColumn,
    wishListSettings,
    productInfo,
    setAddToListModalIsOpen,
    addToWishList,
}) => {
    const [styles] = React.useState(() => mergeToNew(OrderDetailsTableStyle, orderDetailsOrderLineStyles));
    const [showAccordion, setShowAccordion] = useState(false);
    const [showDeliverySchedule, setShowDeliverySchedule] = useState(false);
    const toasterContext = React.useContext(ToasterContext);

    const addToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }

        const productInfo = {
            productId: orderLine.productId,
            qtyOrdered: orderLine.qtyOrdered,
            unitOfMeasure: orderLine.unitOfMeasure,
        };
        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };

    const badges = (orderLine.properties["badges"] && JSON.parse(orderLine.properties["badges"])) || [];
    const orderLineShipmentsData = getOrderLineShipmentData(orderLine.id);
    const orderLineDeliveryScheduleData = orderLine.properties["orderLineDeliveryScheduleDatas"]
        ? JSON.parse(orderLine.properties["orderLineDeliveryScheduleDatas"])
        : [];
    const dateToShow = orderLine.lastShipDate || orderLine.requiredDate;
    const isLastShipDate = !!orderLine.lastShipDate;
    const statusToShow = orderLine.properties.statusDisplay || orderLine.status;

    return (
        <>
            <DataTableRow
                key={orderLine.id}
                {...(showAccordion || showDeliverySchedule ? tableRowWithAccordionStyle : styles.tableRowStyle)}
            >
                <DataTableCell {...styles.tableCellStyle}>
                    <GridContainer gap={0} {...styles.tableCellGridContainerStyle}>
                        <GridItem {...styles.productImageGridStyle} className="image-print">
                            <Link href={orderLine.productUri}>
                                <LazyImage
                                    {...styles.productImageStyle}
                                    src={orderLine.mediumImagePath}
                                    altText={orderLine.altText}
                                    height="82px"
                                    width="82px"
                                />
                            </Link>
                            <StyledWrapper {...styles.badgesContainer}>
                                {badges.map(
                                    (badge: { Image: string | undefined; DisplayName: string; Description: string }) =>
                                        badge.Image && (
                                            <StyledWrapper key={badge.DisplayName}>
                                                <Tooltip
                                                    placement="right"
                                                    passAsChildren={true}
                                                    triggerComponent={
                                                        <StyledWrapper>
                                                            <LazyImage
                                                                src={badge.Image}
                                                                altText={badge.Description}
                                                                {...styles.badgeImage}
                                                            />
                                                        </StyledWrapper>
                                                    }
                                                >
                                                    <StyledWrapper className="popup-content-wrapper">
                                                        {/* eslint-disable react/no-danger */}
                                                        <div
                                                            dangerouslySetInnerHTML={{ __html: badge.Description }}
                                                        ></div>
                                                    </StyledWrapper>
                                                </Tooltip>
                                            </StyledWrapper>
                                        ),
                                )}
                            </StyledWrapper>
                        </GridItem>
                        <GridItem {...styles.productDetailsGridStyle} className="product-details-print">
                            <GridContainer gap={16}>
                                {orderLine.productName ||
                                    (orderLine.description && (
                                        <GridItem {...styles.fullWidthGridStyle}>
                                            <ProductDescription
                                                product={orderLine}
                                                extendedStyles={styles.productDescriptionStyles}
                                            />
                                        </GridItem>
                                    ))}
                                <GridItem {...styles.halfWidthGridStyle}>
                                    <GridContainer>
                                        {orderLine.customerName && (
                                            <GridItem {...styles.fullWidthGridStyle}>
                                                <OrderDetailsInfoDisplay
                                                    title="Cust. Part #"
                                                    value={orderLine.customerName}
                                                    extendedProps={{ contentColor: "primary.main" }}
                                                />
                                            </GridItem>
                                        )}
                                        {orderLine.properties["specNumber"] && (
                                            <GridItem {...styles.fullWidthGridStyle}>
                                                <OrderDetailsInfoDisplay
                                                    title="Spec #:"
                                                    value={orderLine.properties["specNumber"]}
                                                />
                                            </GridItem>
                                        )}
                                        {orderLine.manufacturerItem && (
                                            <GridItem {...styles.fullWidthGridStyle}>
                                                <OrderDetailsInfoDisplay
                                                    title="MFR #"
                                                    value={orderLine.manufacturerItem}
                                                />
                                            </GridItem>
                                        )}
                                        {orderLine.productErpNumber && (
                                            <GridItem {...styles.fullWidthGridStyle}>
                                                <OrderDetailsInfoDisplay
                                                    title="EIS #"
                                                    value={orderLine.productErpNumber}
                                                />
                                            </GridItem>
                                        )}
                                    </GridContainer>
                                </GridItem>
                                <GridItem {...styles.halfWidthGridStyle}>
                                    <GridContainer>
                                        {orderLine.warehouse && (
                                            <GridItem {...styles.fullWidthGridStyle}>
                                                <OrderDetailsInfoDisplay title="Location" value={orderLine.warehouse} />
                                            </GridItem>
                                        )}

                                        {orderLine.lineNumber && (
                                            <GridItem {...styles.fullWidthGridStyle}>
                                                <OrderDetailsInfoDisplay
                                                    title="PO Line #"
                                                    value={orderLine.lineNumber}
                                                />
                                            </GridItem>
                                        )}
                                    </GridContainer>
                                </GridItem>
                            </GridContainer>
                        </GridItem>
                    </GridContainer>
                </DataTableCell>
                <DataTableCell {...styles.tableCellStyle}>
                    <GridContainer gap={0} {...styles.tableCellGridContainerStyle}>
                        <Hidden {...styles.hiddenHeaderStyle}>
                            {/* Order Status */}
                            <GridItem {...styles.tableCellGridStyle}>{headerColumns[1]}</GridItem>
                        </Hidden>
                        <GridItem {...styles.tableCellGridStyle}>
                            <GridContainer gap={16}>
                                {!!statusToShow && (
                                    <GridItem {...styles.customWidthGridStyle}>
                                        <OrderDetailsInfoDisplay
                                            title="Status"
                                            value={translate(statusToShow)}
                                            extendedProps={{
                                                contentColor: orderLine.properties.statusColor,
                                            }}
                                        />
                                    </GridItem>
                                )}
                            </GridContainer>
                        </GridItem>
                    </GridContainer>
                </DataTableCell>
                <DataTableCell {...styles.tableCellStyle}>
                    <GridContainer gap={0} {...styles.tableCellGridContainerStyle}>
                        <Hidden {...styles.hiddenHeaderStyle}>
                            {/* Quantities */}
                            <GridItem {...styles.tableCellGridStyle}>{headerColumns[2]}</GridItem>
                        </Hidden>
                        <GridItem {...styles.tableCellGridStyle}>
                            <GridContainer gap={16}>
                                {!!orderLine.qtyOrdered && (
                                    <GridItem {...styles.customWidthGridStyle}>
                                        <OrderDetailsInfoDisplay title="Ordered" value={orderLine.qtyOrdered} />
                                    </GridItem>
                                )}
                            </GridContainer>
                        </GridItem>
                    </GridContainer>
                </DataTableCell>
                <DataTableCell {...styles.tableCellStyle}>
                    <GridContainer gap={0} {...styles.tableCellGridContainerStyle}>
                        <Hidden {...styles.hiddenHeaderStyle}>
                            {/* Pricing */}
                            <GridItem {...styles.tableCellGridStyle}>{headerColumns[3]}</GridItem>
                        </Hidden>
                        <GridItem {...styles.tableCellGridStyle}>
                            <GridContainer gap={16}>
                                {!!orderLine.unitPriceDisplay && (
                                    <GridItem {...styles.customWidthGridStyle}>
                                        <OrderDetailsInfoDisplay
                                            title="Per Unit"
                                            value={`${orderLine.unitPriceDisplay} / ${orderLine.unitOfMeasure}`}
                                        />
                                    </GridItem>
                                )}
                                {!!orderLine.extendedUnitNetPriceDisplay && (
                                    <GridItem {...styles.customWidthGridStyle}>
                                        <OrderDetailsInfoDisplay
                                            title="Ext. Price"
                                            value={orderLine.extendedUnitNetPriceDisplay}
                                        />
                                    </GridItem>
                                )}
                            </GridContainer>
                        </GridItem>
                    </GridContainer>
                </DataTableCell>
                <DataTableCell {...styles.tableCellStyle} className="hide-print">
                    <GridContainer gap={0} {...styles.tableCellGridContainerStyle}>
                        <Hidden {...styles.hiddenHeaderStyle}>
                            {/* Actions */}
                            <GridItem {...styles.tableCellGridStyle}>{headerColumns[4]}</GridItem>
                        </Hidden>
                        <GridItem {...styles.actionTableCellGridStyle}>
                            {isProductInActive(orderLine) ? (
                                <Typography {...styles.errorMessage}>{translate("Inactive product")}</Typography>
                            ) : (
                                <>
                                    {orderLine.canAddToWishlist && (
                                        <Link {...styles.addToListLink} onClick={addToListClickHandler}>
                                            {translate("Add To Shopping List")}
                                        </Link>
                                    )}
                                    {orderLine && productInfo && (
                                        <ProductContext.Provider
                                            value={
                                                {
                                                    product: { ...orderLine, id: orderLine.productId },
                                                    productInfo,
                                                } as any
                                            }
                                        >
                                            <ProductAddToCartButton
                                                asLink={true}
                                                withIcon={true}
                                                extendedStyles={{
                                                    linkText: { color: "primary.main" },
                                                    addToCartClickable: {
                                                        css: css`
                                                            &:hover,
                                                            &:focus {
                                                                text-decoration: underline;
                                                                ${/* sc-selector */ TypographyStyle}, ${
                                                                    /* sc-selector */ IconWrapper
                                                                } {
                                                                    color: ${({ theme }) =>
                                                                        Color(get(theme, "colors.primary.main"))
                                                                            .darken(0.3)
                                                                            .rgb()
                                                                            .toString()};
                                                                }
                                                            }
                                                        `,
                                                    },
                                                }}
                                            />
                                        </ProductContext.Provider>
                                    )}
                                </>
                            )}
                        </GridItem>
                    </GridContainer>
                </DataTableCell>
                <DataTableCell
                    {...styles.tableCellStyle}
                    className={`hide-print ${showAccordion || showDeliverySchedule ? "hide-on-mobile" : ""}`}
                >
                    <GridContainer gap={0} {...styles.tableCellGridContainerStyle}>
                        <GridItem {...styles.showAccordionGridStyle}>
                            <GridContainer gap={8}>
                                <GridItem width={12}>
                                    {(!!orderLineShipmentsData.length || !!orderLineDeliveryScheduleData.length) && (
                                        <Link
                                            {...styles.shippingDetailsStyle}
                                            icon={{
                                                iconProps: {
                                                    src: showAccordion ? ChevronUp : ChevronDown,
                                                    size: 13,
                                                    color: "primary.main",
                                                    css: css`
                                                        line-height: 0;
                                                        margin-right: 5px;
                                                        vertical-align: middle;
                                                    `,
                                                },
                                                position: "right",
                                            }}
                                            onClick={() => {
                                                if (orderLineShipmentsData.length > 0) {
                                                    setShowAccordion(!showAccordion);
                                                }
                                                if (orderLineDeliveryScheduleData.length > 0) {
                                                    setShowDeliverySchedule(!showDeliverySchedule);
                                                }
                                            }}
                                        >
                                            {translate("Shipping Details")}
                                        </Link>
                                    )}
                                </GridItem>
                            </GridContainer>
                        </GridItem>
                    </GridContainer>
                </DataTableCell>
            </DataTableRow>
            {(showAccordion || showDeliverySchedule) && (
                <DataTableRow key={`shipping-details-${orderLine.id}`} {...styles.accordionRowStyle}>
                    <DataTableCell colSpan={numberOfColumn} {...styles.accordionCellStyle}>
                        <Link
                            {...styles.shippingDetailsStyle}
                            icon={{
                                iconProps: {
                                    src: showAccordion ? ChevronUp : ChevronDown,
                                    size: 13,
                                    color: "primary.main",
                                    css: css`
                                        line-height: 0;
                                        margin-right: 5px;
                                        vertical-align: middle;
                                    `,
                                },
                                position: "right",
                            }}
                            className="hide-on-desktop"
                            onClick={() => {
                                if (orderLineShipmentsData.length > 0) {
                                    setShowAccordion(!showAccordion);
                                }
                                if (orderLineDeliveryScheduleData.length > 0) {
                                    setShowDeliverySchedule(!showDeliverySchedule);
                                }
                            }}
                        >
                            {translate("Shipping Details")}
                        </Link>
                        <OrderDetailsShipmentDetails
                            orderLineShipments={orderLineShipmentsData}
                            unitOfMeasure={orderLine.unitOfMeasure}
                        />
                    </DataTableCell>
                </DataTableRow>
            )}
            {showDeliverySchedule && (
                <DataTableRow key={`delivery-schedule-${orderLine.id}`} {...styles.accordionRowStyle}>
                    <DataTableCell colSpan={numberOfColumn} {...styles.accordionCellStyle}>
                        <OrderDetailsDeliverySchedule
                            deliveryScheduleData={orderLineDeliveryScheduleData}
                            unitOfMeasure={orderLine.unitOfMeasure}
                        />
                    </DataTableCell>
                </DataTableRow>
            )}
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(OrderDetailsOrderLine);
